"use client"

import * as React from "react"
import { CalendarIcon } from "@radix-ui/react-icons"
import { format, startOfMonth, endOfMonth, addMonths } from "date-fns"
import { ChevronLeftIcon, ChevronRightIcon } from "@radix-ui/react-icons";
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { toUtcDate } from "@/lib/utils";

export function DatePicker({
  placeholder,
  value,
  onChange,
  disabled = false,
  minDate,
  maxDate,
  mode = "date",
  monthSelectionType = "start",
}: {
  placeholder: string;
  value?: Date;
  onChange: (date: Date | undefined) => void;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  mode?: "date" | "month";
  monthSelectionType?: "start" | "end";
}) {
  const [date, setDate] = React.useState<Date | undefined>(value)
  const [isOpen, setIsOpen] = React.useState<boolean>(false)
  const [month, setMonth] = React.useState<Date>(value || new Date())

  React.useEffect(() => {
    setDate(value)
    setMonth(value || new Date())
  }, [value])

  const handleDateChange = (selectedDate: Date | undefined) => {
    if (selectedDate) {
      if (mode === "month") {
        selectedDate =
          monthSelectionType === "start"
            ? startOfMonth(selectedDate)
            : endOfMonth(selectedDate)
      }
      if (minDate && selectedDate < minDate) {
        selectedDate = minDate
      }
      if (maxDate && selectedDate > maxDate) {
        selectedDate = maxDate
      }
      const utcDate = new Date(
        Date.UTC(
          selectedDate.getFullYear(),
          selectedDate.getMonth(),
          selectedDate.getDate()
        )
      )
      
      setDate(utcDate)
      onChange(utcDate)
      setIsOpen(false)
    } else {
      setDate(undefined)
      onChange(undefined)
    }
  }

  const handleTodayClick = () => {
    const today = new Date()
    handleDateChange(today)
  }

  const handleResetClick = () => {
    handleDateChange(undefined)
  }

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (open) {
      setMonth(date || new Date())
    }
  }

  const renderMonthPicker = () => {
    const [currentYear, setCurrentYear] = React.useState(month.getFullYear())

    const months = Array.from({ length: 12 }, (_, i) =>
      addMonths(new Date(currentYear, 0, 1), i)
    )

    const handleYearChange = (direction: "prev" | "next") => {
      setCurrentYear((prevYear) =>
        direction === "prev" ? prevYear - 1 : prevYear + 1
      )
    }

    return (
      <div className="p-4 h-full">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => handleYearChange("prev")}
            className="p-2 rounded-md hover:bg-gray-200"
          >
            <ChevronLeftIcon className="w-6 h-6 text-gray-600 dark:text-darkTextColor" />
          </button>
          <span className="text-lg font-semibold">{currentYear}</span>
          <button
            onClick={() => handleYearChange("next")}
            className="p-2 rounded-md hover:bg-gray-200"
          >
            <ChevronRightIcon className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        <div className="grid grid-cols-3 gap-4">
          {months.map((monthDate) => {
            const isDisabled =
              (minDate && monthDate < startOfMonth(minDate)) ||
              (maxDate && monthDate > endOfMonth(maxDate))

            const isSelected =
              date &&
              monthDate.getMonth() === date.getMonth() &&
              monthDate.getFullYear() === date.getFullYear()

            return (
              <button
                key={monthDate.toISOString()}
                onClick={() => handleDateChange(monthDate)}
                disabled={isDisabled}
                className={cn(
                  "p-4 rounded-lg border border-border text-center transition-colors duration-200",
                  isDisabled
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : isSelected
                    ? "bg-blue-100 border-blue-500 text-blue-700"
                    : "bg-white dark:bg-background border border-border hover:bg-gray-300 dark:hover:bg-gray-500 cursor-pointer"
                )}
                style={{ height: "40px", display: "flex", justifyContent: "center", alignItems: "center" }}
              >
                {format(monthDate, "MMM")}
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant={"outline"}
          className={cn(
            "dark:text-darkTextColor border border-border w-[240px] justify-start text-left font-normal",
            !date && "text-muted-foreground",
            disabled && "pointer-events-none opacity-50"
          )}
          disabled={disabled}
          onClick={() => setIsOpen(!isOpen)}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {date ? format(date, mode === "month" ? "MMMM yyyy" : "PPP") : <span className="dark:text-darkTextColor border border-border">{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      {!disabled && (
        <PopoverContent
          className="w-auto p-0 z-[1050]"
          align="start"
          style={{ zIndex: 1050, pointerEvents: "auto" }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <div
            style={{
              minWidth: mode === "month" ? "250px" : "300px",
              minHeight: mode === "month" ? "200px" : "350px",
              pointerEvents: "auto",
            }}
          >
            {mode === "month" ? (
              renderMonthPicker()
            ) : (
              <Calendar
                mode="single"
                selected={date}
                onSelect={handleDateChange}
                initialFocus
                month={month}
                onMonthChange={(newMonth) => setMonth(newMonth)}
                disabled={(date) =>
                  (minDate && date < minDate) || (maxDate && date > maxDate)
                    ? true
                    : false
                }
              />
            )}
            <div className="flex justify-between p-4">
              <Button onClick={handleTodayClick} variant="outline">
                Today
              </Button>
              <Button onClick={handleResetClick} variant="outline" color="red">
                Reset
              </Button>
            </div>
          </div>
        </PopoverContent>
      )}
    </Popover>
  )
}
